import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import waitlistApi from '../../services/waitlistApi';
import * as ROUTES from '../../constants/routes';
import './WaitlistConfirm.css';

const STATUS = {
  LOADING: 'loading',
  CONFIRMED: 'confirmed',
  ERROR: 'error',
};

const WaitlistConfirm = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const email = searchParams.get('email');

  const [status, setStatus] = useState(STATUS.LOADING);
  const calledRef = useRef(false);

  useEffect(() => {
    // Guard against StrictMode double-invocation in dev.
    if (calledRef.current) return;
    calledRef.current = true;

    if (!token) {
      setStatus(STATUS.ERROR);
      return;
    }

    let cancelled = false;

    waitlistApi
      .confirm(token)
      .then((data) => {
        if (cancelled) return;
        // 200 response => verified (idempotent). Treat as confirmed.
        if (data && data.status === 'verified') {
          setStatus(STATUS.CONFIRMED);
        } else {
          // Unexpected payload shape — still treat as success if HTTP was ok.
          setStatus(STATUS.CONFIRMED);
        }
      })
      .catch(() => {
        if (cancelled) return;
        setStatus(STATUS.ERROR);
      });

    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <div className="waitlist-confirm-page">
      <div className="waitlist-confirm-card" role="main" aria-live="polite">
        <span className="waitlist-confirm-badge" aria-label="Replan360">
          Replan360
        </span>

        {status === STATUS.LOADING && (
          <>
            <div className="waitlist-confirm-spinner" aria-hidden="true" />
            <h1 className="waitlist-confirm-title">
              {t('waitlist.confirming')}
            </h1>
            <p className="waitlist-confirm-body">
              {email ? email : ''}
            </p>
          </>
        )}

        {(status === STATUS.CONFIRMED) && (
          <>
            <div className="waitlist-confirm-icon" aria-hidden="true">
              ✓
            </div>
            <h1 className="waitlist-confirm-title">
              {t('waitlist.confirmed_title')}
            </h1>
            <p className="waitlist-confirm-body">
              {t('waitlist.confirmed_body')}
            </p>
            <Link to={ROUTES.LANDING} className="waitlist-confirm-home-link">
              {t('waitlist.back_home')}
            </Link>
          </>
        )}

        {status === STATUS.ERROR && (
          <>
            <div className="waitlist-confirm-icon waitlist-confirm-icon--error" aria-hidden="true">
              !
            </div>
            <h1 className="waitlist-confirm-title">
              {t('waitlist.invalid_title')}
            </h1>
            <p className="waitlist-confirm-body">
              {t('waitlist.invalid_body')}
            </p>
            <Link to={ROUTES.LANDING} className="waitlist-confirm-home-link">
              {t('waitlist.back_home')}
            </Link>
          </>
        )}
      </div>

      <div className="waitlist-confirm-footer">
        © {new Date().getFullYear()} Replan360.com
      </div>
    </div>
  );
};

export default WaitlistConfirm;
